import { useCallback, useEffect, useState } from "react";
import { api, apiError, rupiah, waktu } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ErpLayout, Card, Table } from "@/erp/shell";
import { toast } from "sonner";
import { Download, RefreshCw, Plus, Lock } from "lucide-react";

const inp = "w-full rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";

const TABS = [
  ["ringkasan", "Ringkasan"], ["coa", "Chart of Accounts"], ["jurnal", "Jurnal"],
  ["piutang", "Piutang & AR Aging"], ["vendor", "Vendor & Bills"], ["biaya", "Biaya"],
  ["laporan", "Laporan"], ["periode", "Periode & Budget"],
];

export default function FinanceAdmin() {
  const { can } = useAuth();
  const [tab, setTab] = useState("ringkasan");
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState({ items: [], total: 0 });
  const [jq, setJq] = useState("");
  const [jpage, setJpage] = useState(1);
  const [jdetail, setJdetail] = useState(null);
  const [ar, setAr] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [bills, setBills] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [report, setReport] = useState("trial-balance");
  const [reportData, setReportData] = useState(null);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get("/finance/reports/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/finance/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/finance/reports/ar-aging").then((r) => setAr(r.data)).catch(() => {});
    api.get("/finance/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/finance/bills").then((r) => setBills(r.data)).catch(() => {});
    api.get("/finance/expenses").then((r) => setExpenses(r.data)).catch(() => {});
    api.get("/finance/periods").then((r) => setPeriods(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setJdetail(null); setForm(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    api.get("/finance/journals", { params: { q: jq || undefined, page: jpage, page_size: 10 } })
      .then((r) => setJournals(r.data)).catch(() => {});
  }, [jq, jpage]);
  useEffect(() => {
    if (tab !== "laporan") return;
    api.get(`/finance/reports/${report}`).then((r) => setReportData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [tab, report]);

  const openJournal = async (id) => {
    try { const { data } = await api.get(`/finance/journals/${id}`); setJdetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reverse = async (id) => {
    const reason = window.prompt("Alasan pembalikan jurnal?");
    if (!reason) return;
    try { await api.post(`/finance/journals/${id}/reverse`, { reason });
      toast.success("Jurnal pembalik dibuat"); setJdetail(null); setJpage(1); load();
      const { data } = await api.get("/finance/journals", { params: { page: 1, page_size: 10 } });
      setJournals(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportReport = async (fmt) => {
    try {
      const res = await api.get(`/finance/reports/${report}/export`, { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (fmt === "pdf") window.open(url, "_blank");
      else { const a = document.createElement("a"); a.href = url; a.download = `${report}.csv`; a.click(); }
    } catch (e) { toast.error(apiError(e)); }
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(form._url, form._body(form));
      toast.success("Tersimpan"); setForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const togglePeriod = async (p, field) => {
    try {
      await api.put("/finance/periods", { year: p.year, month: p.month,
        is_locked: field === "is_locked" ? !p.is_locked : p.is_locked,
        is_closed: field === "is_closed" ? !p.is_closed : p.is_closed });
      toast.success("Periode diperbarui"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="Keuangan (Double-Entry)" breadcrumb={["Keuangan"]}>
      <div className="mb-5 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`fin-tab-${k}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-[#141414] text-white" : "border border-[#E5DEC9] bg-white"}`}>{l}</button>))}
      </div>

      {tab === "ringkasan" && summary && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="finance-summary">
            {[["Pendapatan", summary.revenue], ["Beban", summary.expense],
              ["Laba Bersih", summary.net_income], ["Kas & Bank", summary.cash_bank],
              ["Piutang", summary.receivable], ["Utang Komisi", summary.commission_payable],
              ["Pembayaran Terverifikasi", summary.verified_payments],
              ["Tagihan Outstanding", summary.outstanding]].map(([k, v]) => (
              <Card key={k}><div className="eyebrow">{k}</div>
                <div className="mt-2 font-display text-xl font-bold text-[#141414]">{rupiah(v)}</div></Card>))}
          </div>
          <Card className="mt-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><div className="eyebrow">Integritas Buku</div>
                <div className="mt-1 text-sm">{summary.journal_count} jurnal terposting</div></div>
              <span className={`rounded-full px-3 py-1.5 text-sm font-bold ${summary.is_balanced ? "bg-[#F5F1E8] text-[#15803D]" : "bg-[#FBE9E9] text-[#B91C1C]"}`}
                data-testid="ledger-balanced">{summary.is_balanced ? "Semua jurnal balance" : "TIDAK BALANCE"}</span>
            </div>
            <p className="mt-3 text-xs text-[#8C6D1F]">Catatan: seed Chart of Accounts awal — konfigurasi final harus divalidasi akuntan.</p>
          </Card>
        </>)}

      {tab === "coa" && (
        <>
          {can("finance.write") && (
            <button onClick={() => setForm({ _title: "Akun Baru", _url: "/finance/accounts",
              _body: (f) => ({ code: f.code, name: f.name, type: f.type, normal_balance: f.normal_balance }),
              code: "", name: "", type: "expense", normal_balance: "debit",
              _fields: [["code", "Kode akun", "text"], ["name", "Nama akun", "text"],
                        ["type", "Tipe", "select", ["asset", "liability", "equity", "revenue", "expense"]],
                        ["normal_balance", "Saldo normal", "select", ["debit", "credit"]]] })}
              data-testid="coa-new-btn" className="btn-gold mb-4 rounded-xl px-4 py-2.5 text-sm font-bold"><Plus size={14} className="mr-1 inline" />Akun Baru</button>)}
          <Table testid="coa-table" columns={["Kode", "Nama Akun", "Tipe", "Saldo Normal"]}
            rows={accounts.map((a) => (
              <tr key={a.code} className="border-t border-[#F4EFE6]" data-testid={`coa-row-${a.code}`}>
                <td className="p-4 font-semibold">{a.code}</td><td className="p-4">{a.name}</td>
                <td className="p-4 capitalize">{a.type}</td><td className="p-4 capitalize">{a.normal_balance}</td>
              </tr>))} />
        </>)}

      {tab === "jurnal" && (
        <>
          <Card className="mb-4"><input className={`${inp} max-w-sm`} placeholder="Cari nomor/memo jurnal..."
            data-testid="journal-search" value={jq} onChange={(e) => { setJq(e.target.value); setJpage(1); }} /></Card>
          <Table testid="journal-table" columns={["Nomor", "Tanggal", "Memo", "Purpose", "Debit", "Kredit", "Status"]}
            rows={journals.items.map((j) => (
              <tr key={j.id} className="cursor-pointer border-t border-[#F4EFE6] hover:bg-[#FBF9F4]"
                onClick={() => openJournal(j.id)} data-testid={`journal-row-${j.id}`}>
                <td className="p-4 font-semibold">{j.journal_number}</td>
                <td className="p-4">{String(j.date).slice(0, 10)}</td>
                <td className="p-4">{j.memo}</td>
                <td className="p-4 text-xs">{j.purpose}</td>
                <td className="p-4">{rupiah(j.total_debit)}</td>
                <td className="p-4">{rupiah(j.total_credit)}</td>
                <td className="p-4">{j.reversed_by ? <span className="text-xs text-[#B91C1C]">dibalik</span>
                  : j.is_reversal ? <span className="text-xs">pembalik</span>
                  : <span className="text-xs font-bold text-[#15803D]">posted</span>}</td>
              </tr>))} />
          <div className="mt-4 flex items-center gap-2">
            <button disabled={jpage <= 1} onClick={() => setJpage(jpage - 1)} data-testid="journal-prev" className="rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 text-sm disabled:opacity-40">Sebelumnya</button>
            <span className="text-sm">Halaman {jpage} · {journals.total} jurnal</span>
            <button disabled={jpage * 10 >= journals.total} onClick={() => setJpage(jpage + 1)} data-testid="journal-next" className="rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 text-sm disabled:opacity-40">Berikutnya</button>
          </div>
        </>)}

      {tab === "piutang" && ar && (
        <>
          <div className="grid gap-4 sm:grid-cols-4" data-testid="ar-buckets">
            {Object.entries(ar.buckets).map(([k, v]) => (
              <Card key={k}><div className="eyebrow">{k} hari</div>
                <div className="mt-2 font-display text-xl font-bold text-[#141414]">{rupiah(v)}</div></Card>))}
          </div>
          <Table testid="ar-table" columns={["Invoice", "Pelanggan", "Total", "Dibayar", "Sisa", "Umur", "Bucket"]}
            rows={ar.items.map((i) => (
              <tr key={i.id} className="border-t border-[#F4EFE6]" data-testid={`ar-row-${i.id}`}>
                <td className="p-4 font-semibold">{i.invoice_number}</td><td className="p-4">{i.customer_name}</td>
                <td className="p-4">{rupiah(i.total)}</td><td className="p-4">{rupiah(i.paid_amount)}</td>
                <td className="p-4 font-bold text-[#B45309]">{rupiah(i.balance)}</td>
                <td className="p-4">{i.age_days} hari</td><td className="p-4">{i.bucket}</td>
              </tr>))} />
        </>)}

      {tab === "vendor" && (
        <>
          {can("finance.write") && (
            <div className="mb-4 flex gap-2">
              <button onClick={() => setForm({ _title: "Vendor Baru", _url: "/finance/vendors",
                _body: (f) => ({ name: f.name, phone: f.phone, bank_name: f.bank_name, bank_account: f.bank_account }),
                name: "", phone: "", bank_name: "", bank_account: "",
                _fields: [["name", "Nama vendor", "text"], ["phone", "Telepon", "text"],
                          ["bank_name", "Bank", "text"], ["bank_account", "Rekening", "text"]] })}
                data-testid="vendor-new-btn" className="rounded-xl border border-[#141414] px-4 py-2.5 text-sm font-semibold text-[#141414]">Vendor Baru</button>
              <button onClick={() => setForm({ _title: "Tagihan Vendor", _url: "/finance/bills",
                _body: (f) => ({ vendor_id: f.vendor_id, description: f.description,
                                 amount: Number(f.amount), due_date: f.due_date,
                                 expense_account_code: f.expense_account_code }),
                vendor_id: vendors[0]?.id || "", description: "", amount: 0, due_date: "",
                expense_account_code: "5200",
                _fields: [["vendor_id", "Vendor", "select", vendors.map((v) => v.id), vendors.map((v) => v.name)],
                          ["description", "Deskripsi", "text"], ["amount", "Nominal", "number"],
                          ["due_date", "Jatuh tempo", "date"],
                          ["expense_account_code", "Akun beban", "select",
                           accounts.filter((a) => a.type === "expense").map((a) => a.code),
                           accounts.filter((a) => a.type === "expense").map((a) => `${a.code} ${a.name}`)]] })}
                data-testid="bill-new-btn" className="btn-gold rounded-xl px-4 py-2.5 text-sm font-bold">Tagihan Baru</button>
            </div>)}
          <Table testid="bill-table" columns={["Nomor", "Vendor", "Deskripsi", "Nominal", "Status", "Aksi"]}
            rows={bills.map((b) => (
              <tr key={b.id} className="border-t border-[#F4EFE6]" data-testid={`bill-row-${b.id}`}>
                <td className="p-4 font-semibold">{b.bill_number}</td><td className="p-4">{b.vendor_name}</td>
                <td className="p-4">{b.description}</td><td className="p-4">{rupiah(b.amount)}</td>
                <td className="p-4">{b.status}</td>
                <td className="p-4">{can("finance.write") && b.status === "open" && (
                  <button onClick={async () => {
                    try { await api.post(`/finance/bills/${b.id}/pay`); toast.success("Tagihan dibayar & jurnal dibuat"); load(); }
                    catch (e) { toast.error(apiError(e)); }
                  }} data-testid={`bill-pay-${b.id}`} className="btn-gold rounded-lg px-3 py-1.5 text-xs font-bold">Bayar</button>)}</td>
              </tr>))} />
        </>)}

      {tab === "biaya" && (
        <>
          {can("finance.write") && (
            <button onClick={() => setForm({ _title: "Biaya Baru", _url: "/finance/expenses",
              _body: (f) => ({ description: f.description, amount: Number(f.amount),
                               account_code: f.account_code, paid_from: f.paid_from }),
              description: "", amount: 0, account_code: "5200", paid_from: "bank",
              _fields: [["description", "Deskripsi", "text"], ["amount", "Nominal", "number"],
                        ["account_code", "Akun beban", "select",
                         accounts.filter((a) => a.type === "expense").map((a) => a.code),
                         accounts.filter((a) => a.type === "expense").map((a) => `${a.code} ${a.name}`)],
                        ["paid_from", "Dibayar dari", "select", ["bank", "cash"]]] })}
              data-testid="expense-new-btn" className="btn-gold mb-4 rounded-xl px-4 py-2.5 text-sm font-bold"><Plus size={14} className="mr-1 inline" />Biaya Baru</button>)}
          <Table testid="expense-table" columns={["Nomor", "Deskripsi", "Akun", "Nominal", "Status", "Aksi"]}
            rows={expenses.map((x) => (
              <tr key={x.id} className="border-t border-[#F4EFE6]" data-testid={`expense-row-${x.id}`}>
                <td className="p-4 font-semibold">{x.expense_number}</td><td className="p-4">{x.description}</td>
                <td className="p-4">{x.account_code}</td><td className="p-4">{rupiah(x.amount)}</td>
                <td className="p-4">{x.status}</td>
                <td className="p-4">{can("finance.write") && x.status === "submitted" && (
                  <button onClick={async () => {
                    try { await api.post(`/finance/expenses/${x.id}/approve`); toast.success("Biaya disetujui & jurnal dibuat"); load(); }
                    catch (e) { toast.error(apiError(e)); }
                  }} data-testid={`expense-approve-${x.id}`} className="btn-gold rounded-lg px-3 py-1.5 text-xs font-bold">Setujui</button>)}</td>
              </tr>))} />
        </>)}

      {tab === "laporan" && (
        <>
          <Card className="mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <select className={`${inp} max-w-xs`} data-testid="report-select" value={report}
                onChange={(e) => setReport(e.target.value)}>
                {["trial-balance", "general-ledger", "profit-loss", "balance-sheet", "cash-flow",
                  "ar-aging", "ap-aging", "outstanding-invoices", "payment-recap",
                  "revenue-by-product", "commission", "budget-vs-actual"].map((r) => (
                  <option key={r} value={r}>{r.replace(/-/g, " ")}</option>))}
              </select>
              <button onClick={() => exportReport("csv")} data-testid="export-csv" className="flex items-center gap-1 rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm font-semibold"><Download size={13} /> CSV</button>
              <button onClick={() => exportReport("pdf")} data-testid="export-pdf" className="flex items-center gap-1 rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm font-semibold"><Download size={13} /> PDF</button>
            </div>
          </Card>
          <Card data-testid="report-output">
            <pre className="max-h-[520px] overflow-auto text-xs text-[#4A4844]">{JSON.stringify(reportData, null, 2)}</pre>
          </Card>
        </>)}

      {tab === "periode" && (
        <Table testid="period-table" columns={["Tahun", "Bulan", "Status", "Aksi"]}
          rows={periods.map((p) => (
            <tr key={`${p.year}-${p.month}`} className="border-t border-[#F4EFE6]" data-testid={`period-row-${p.year}-${p.month}`}>
              <td className="p-4">{p.year}</td><td className="p-4">{String(p.month).padStart(2, "0")}</td>
              <td className="p-4">{p.is_closed ? "closed" : p.is_locked ? "locked" : "open"}</td>
              <td className="p-4">{can("finance.write") && (
                <div className="flex gap-2">
                  <button onClick={() => togglePeriod(p, "is_locked")} data-testid={`period-lock-${p.year}-${p.month}`}
                    className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold"><Lock size={11} className="mr-1 inline" />{p.is_locked ? "Buka" : "Kunci"}</button>
                  <button onClick={() => togglePeriod(p, "is_closed")} data-testid={`period-close-${p.year}-${p.month}`}
                    className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold">{p.is_closed ? "Buka Tutup" : "Tutup"}</button>
                </div>)}</td>
            </tr>))} />)}

      {jdetail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
          data-testid="journal-detail-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setJdetail(null); }}>
          <Card className="my-8 w-full max-w-2xl" data-testid="journal-detail">
            <div className="flex items-start justify-between">
              <div><div className="eyebrow">{jdetail.journal_number}</div>
                <h2 className="font-display text-lg font-bold text-[#141414]">{jdetail.memo}</h2>
                <div className="text-xs text-[#8A867C]">{jdetail.purpose} · {waktu(jdetail.date)} · oleh {jdetail.posted_by_email}</div></div>
              <button onClick={() => setJdetail(null)}>✕</button>
            </div>
            <table className="mt-4 w-full text-sm">
              <thead className="bg-[#F4EFE6] text-left text-xs uppercase"><tr><th className="p-2">Akun</th><th className="p-2">Keterangan</th><th className="p-2 text-right">Debit</th><th className="p-2 text-right">Kredit</th></tr></thead>
              <tbody>
                {(jdetail.lines || []).map((l) => (
                  <tr key={l.id} className="border-t border-[#F4EFE6]">
                    <td className="p-2">{l.account_code} {l.account_name}</td><td className="p-2">{l.description}</td>
                    <td className="p-2 text-right">{l.debit ? rupiah(l.debit) : "-"}</td>
                    <td className="p-2 text-right">{l.credit ? rupiah(l.credit) : "-"}</td>
                  </tr>))}
                <tr className="border-t-2 border-[#141414] font-bold">
                  <td className="p-2" colSpan={2}>Total</td>
                  <td className="p-2 text-right">{rupiah(jdetail.total_debit)}</td>
                  <td className="p-2 text-right">{rupiah(jdetail.total_credit)}</td>
                </tr>
              </tbody>
            </table>
            <p className="mt-3 text-xs text-[#8A867C]">Jurnal terposting bersifat immutable — koreksi hanya melalui pembalikan (reversal).</p>
            {can("journal.post") && !jdetail.reversed_by && !jdetail.is_reversal && (
              <button onClick={() => reverse(jdetail.id)} data-testid="journal-reverse"
                className="mt-4 flex items-center gap-2 rounded-xl border border-[#F0C4C4] px-4 py-2.5 text-sm font-semibold text-[#B91C1C]"><RefreshCw size={14} /> Balik Jurnal</button>)}
          </Card>
        </div>)}

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <form onSubmit={save} className="my-8 w-full max-w-lg rounded-2xl bg-white p-7" data-testid="finance-form">
            <h2 className="font-display text-xl font-bold text-[#141414]">{form._title}</h2>
            <div className="mt-5 space-y-3">
              {form._fields.map(([k, label, type, options, optionLabels]) => (
                <div key={k}>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#4A4844]">{label}</label>
                  {type === "select" ? (
                    <select className={inp} data-testid={`ff-${k}`} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}>
                      {(options || []).map((o, i) => <option key={o} value={o}>{(optionLabels || options)[i]}</option>)}
                    </select>
                  ) : (
                    <input type={type} className={inp} data-testid={`ff-${k}`} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })} />)}
                </div>))}
            </div>
            <div className="mt-6 flex gap-3">
              <button className="btn-gold rounded-xl px-6 py-3 text-sm font-bold" data-testid="finance-form-submit">Simpan</button>
              <button type="button" onClick={() => setForm(null)} className="rounded-xl border border-[#E5DEC9] px-6 py-3 text-sm font-semibold">Batal</button>
            </div>
          </form>
        </div>)}
    </ErpLayout>
  );
}

export function PartnerPortal() {
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [comm, setComm] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get("/partner/me").then((r) => setMe(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/partner/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/partner/commissions").then((r) => setComm(r.data)).catch(() => {});
    api.get("/partner/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/partner/payouts", { ...form, amount: Number(form.amount) });
      toast.success("Pengajuan pencairan terkirim, saldo direservasi");
      setForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const exportCsv = () => {
    const rows = comm?.items || [];
    const csv = ["kode,mitra,level,persen,nominal,status"].concat(
      rows.map((r) => [r.booking_code, r.partner_name, r.level, r.percent, r.amount, r.status].join(","))).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "komisi-saya.csv"; a.click();
  };

  const link = me?.referral_code ? `${window.location.origin}/?ref=${me.referral_code}` : null;

  return (
    <ErpLayout title="Portal Mitra" breadcrumb={["Portal Mitra"]}>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="partner-referral">
          <div className="eyebrow">Kode & Link Referral</div>
          <div className="mt-2 font-display text-2xl font-bold text-[#141414]" data-testid="partner-referral-code">{me?.referral_code || "-"}</div>
          {link && (<>
            <div className="mt-2 break-all rounded-lg bg-[#F4EFE6] p-3 text-xs" data-testid="partner-referral-link">{link}</div>
            <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link disalin"); }}
              data-testid="copy-referral" className="mt-3 rounded-lg border border-[#141414] px-4 py-2 text-xs font-bold text-[#141414]">Salin Link</button>
          </>)}
          <div className="mt-4 text-sm text-[#4A4844]">
            {me?.branch && <>Cabang: <b>{me.branch.name}</b> ({me.branch.type === "cabang" ? "Cabang" : "Cabang Pembantu"})</>}
          </div>
        </Card>
        <Card data-testid="partner-commission-summary">
          <div className="eyebrow">Saldo Komisi</div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            {["earned", "available", "payout_requested", "approved", "paid"].map((k) => (
              <div key={k} className="rounded-lg border border-[#E5DEC9] p-3">
                <div className="text-xs uppercase text-[#8A867C]">{k.replace(/_/g, " ")}</div>
                <div className="mt-1 font-bold text-[#141414]" data-testid={`comm-${k}`}>{rupiah(comm?.summary?.[k] || 0)}</div>
              </div>))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => setForm({ amount: comm?.withdrawable || 0,
              bank_name: me?.bank?.bank_name || "", bank_account: me?.bank?.bank_account || "",
              account_holder: me?.bank?.account_holder || "", note: "" })}
              disabled={!comm?.withdrawable} data-testid="payout-request-btn"
              className="btn-gold rounded-xl px-4 py-2.5 text-sm font-bold disabled:opacity-40">Ajukan Pencairan</button>
            <button onClick={exportCsv} data-testid="partner-export" className="rounded-xl border border-[#E5DEC9] px-4 py-2.5 text-sm font-semibold">Export CSV</button>
          </div>
        </Card>
      </div>

      {bookings && (
        <>
          <h2 className="mt-8 font-display text-xl font-semibold text-[#141414]">Funnel Booking</h2>
          <div className="mt-3 flex flex-wrap gap-3" data-testid="partner-funnel">
            {Object.entries(bookings.funnel).map(([k, v]) => (
              <div key={k} className="rounded-xl border border-[#E5DEC9] bg-white px-4 py-3 text-sm">
                <div className="text-xs uppercase text-[#8A867C]">{k.replace(/_/g, " ")}</div>
                <div className="mt-1 font-bold text-[#141414]">{v}</div>
              </div>))}
            {Object.keys(bookings.funnel).length === 0 && <Card className="text-sm text-[#8A867C]">Belum ada booking dalam hierarki Anda.</Card>}
          </div>
          <h2 className="mt-8 font-display text-xl font-semibold text-[#141414]">Jamaah dalam Scope Saya</h2>
          <div className="mt-3">
            <Table testid="partner-bookings-table" columns={["Kode", "Jamaah", "Paket", "Pembayaran", "Dokumen", "Status"]}
              rows={bookings.items.map((b) => (
                <tr key={b.id} className="border-t border-[#F4EFE6]" data-testid={`partner-booking-${b.id}`}>
                  <td className="p-4 font-semibold">{b.booking_code}</td>
                  <td className="p-4">{b.contact_name}<div className="text-xs text-[#8A867C]">{b.pax} jamaah</div></td>
                  <td className="p-4">{b.product_name}</td>
                  <td className="p-4 capitalize">{b.payment_status}</td>
                  <td className="p-4">{b.document_completeness.percent}% lengkap</td>
                  <td className="p-4 text-xs">{String(b.status).replace(/_/g, " ")}</td>
                </tr>))} />
            <p className="mt-2 text-xs text-[#8A867C]">Mitra hanya melihat status kelengkapan dokumen, bukan file dokumen sensitif.</p>
          </div>
        </>)}

      <h2 className="mt-8 font-display text-xl font-semibold text-[#141414]">Riwayat Pencairan</h2>
      <div className="mt-3">
        <Table testid="partner-payouts-table" columns={["Nomor", "Nominal", "Rekening", "Status", "Diajukan"]}
          rows={payouts.map((p) => (
            <tr key={p.id} className="border-t border-[#F4EFE6]" data-testid={`partner-payout-${p.id}`}>
              <td className="p-4 font-semibold">{p.request_number}</td>
              <td className="p-4">{rupiah(p.amount)}</td>
              <td className="p-4 text-xs">{p.bank_name} {p.bank_account}</td>
              <td className="p-4 text-xs capitalize">{String(p.status).replace(/_/g, " ")}
                {p.rejection_reason && <div className="text-[#B91C1C]">{p.rejection_reason}</div>}</td>
              <td className="p-4 text-xs">{waktu(p.created_at)}</td>
            </tr>))} />
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <form onSubmit={submit} className="my-8 w-full max-w-md rounded-2xl bg-white p-7" data-testid="payout-form">
            <h2 className="font-display text-xl font-bold text-[#141414]">Ajukan Pencairan Komisi</h2>
            <p className="mt-1 text-xs text-[#8A867C]">Saldo available: {rupiah(comm?.withdrawable || 0)}. Saldo langsung direservasi agar tidak diajukan dua kali.</p>
            <div className="mt-5 space-y-3">
              {[["amount", "Nominal (IDR)", "number"], ["bank_name", "Nama bank", "text"],
                ["bank_account", "Nomor rekening", "text"], ["account_holder", "Atas nama", "text"],
                ["note", "Catatan", "text"]].map(([k, label, t]) => (
                <div key={k}><label className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#4A4844]">{label}</label>
                  <input type={t} required={k !== "note"} className={inp} data-testid={`po-${k}`} value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>))}
            </div>
            <div className="mt-6 flex gap-3">
              <button className="btn-gold rounded-xl px-6 py-3 text-sm font-bold" data-testid="po-submit">Ajukan</button>
              <button type="button" onClick={() => setForm(null)} className="rounded-xl border border-[#E5DEC9] px-6 py-3 text-sm font-semibold">Batal</button>
            </div>
          </form>
        </div>)}
    </ErpLayout>
  );
}
